import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { FaCloud, FaLayerGroup, FaMapMarkerAlt, FaUserCircle } from 'react-icons/fa';

const CloudMapTenantView = ({ resources, summary, tenant, onOrgSelect, onStandaloneSelect }) => {
  const { orgMetrics, standaloneMetrics } = useMemo(() => {
    const orgMap = {};
    const standalone = { count: 0, types: new Set(), regions: new Set(), accounts: new Set() };
    
    const orgs = tenant?.cloudAccounts?.organizations || [];
    const standaloneAccounts = tenant?.cloudAccounts?.aws || [];
    
    orgs.forEach(org => {
      orgMap[org.orgId] = { count: 0, types: new Set(), regions: new Set(), accounts: new Set() };
    });
    
    resources.forEach(resource => {
      const accountId = resource.metadata?.accountId;
      if (!accountId) return;
      
      let foundInOrg = false;
      orgs.forEach(org => {
        const isManagement = org.id === accountId;
        const isMember = org.memberAccounts?.some(m => m.accountId === accountId);
        
        if (isManagement || isMember) {
          orgMap[org.orgId].count++;
          orgMap[org.orgId].types.add(resource.type);
          orgMap[org.orgId].regions.add(resource.region);
          orgMap[org.orgId].accounts.add(accountId);
          foundInOrg = true;
        }
      });
      
      if (!foundInOrg && standaloneAccounts.some(acc => acc.accountId === accountId)) {
        standalone.count++;
        standalone.types.add(resource.type);
        standalone.regions.add(resource.region);
        standalone.accounts.add(accountId);
      }
    });
    
    return { orgMetrics: orgMap, standaloneMetrics: standalone };
  }, [resources, tenant]);

  const organizations = tenant?.cloudAccounts?.organizations || [];
  const standaloneAccounts = tenant?.cloudAccounts?.aws || [];

  return (
    <div className="container">
      <Row className="mb-4">
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(109, 91, 255, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaCloud /></div>
              <h3 className="mb-0">{summary?.totalResources || 0}</h3>
              <small style={{ opacity: 0.9 }}>Total Resources</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaLayerGroup /></div>
              <h3 className="mb-0">{summary?.resourceTypes?.length || 0}</h3>
              <small style={{ opacity: 0.9 }}>Resource Types</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaMapMarkerAlt /></div>
              <h3 className="mb-0">{organizations.length}</h3>
              <small style={{ opacity: 0.9 }}>Organizations</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaUserCircle /></div>
              <h3 className="mb-0">{standaloneAccounts.length}</h3>
              <small style={{ opacity: 0.9 }}>Standalone Accounts</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="mb-3">
        <h5 style={{ color: '#1e293b', fontWeight: '600' }}>Organizations & Accounts</h5>
        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Select an organization or standalone accounts to view resources</p>
      </div>

      <Row>
        {standaloneAccounts.length > 0 && (
          <Col md={6} className="mb-3">
            <Card onClick={onStandaloneSelect} style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)' }}>
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginRight: '12px' }}>
                    <FaUserCircle />
                  </div>
                  <div>
                    <h6 className="mb-0" style={{ fontSize: '1rem' }}>Standalone Accounts</h6>
                    <small style={{ opacity: 0.8, fontSize: '0.75rem' }}>Individual AWS accounts</small>
                  </div>
                </div>
                <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Resources</span>
                    <strong style={{ fontSize: '1.25rem' }}>{standaloneMetrics.count}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Types</span>
                    <strong>{standaloneMetrics.types.size}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Accounts</span>
                    <strong>{standaloneMetrics.accounts.size}</strong>
                  </div>
                </div>
                <div className="text-center" style={{ fontSize: '0.875rem', opacity: 0.9 }}>Click to view details →</div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {organizations.map((org) => {
          const metrics = orgMetrics[org.orgId] || { count: 0, types: new Set(), regions: new Set(), accounts: new Set() };
          return (
            <Col md={6} key={org.orgId} className="mb-3">
              <Card onClick={() => onOrgSelect(org.orgId)} style={{ background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)', color: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(109, 91, 255, 0.2)' }}>
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginRight: '12px' }}>
                      <FaLayerGroup />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="mb-0" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.alias || 'AWS Organization'}</h6>
                      <small style={{ opacity: 0.8, fontSize: '0.75rem' }}>{org.orgId}</small>
                    </div>
                  </div>
                  <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Resources</span>
                      <strong style={{ fontSize: '1.25rem' }}>{metrics.count}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Types</span>
                      <strong>{metrics.types.size}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Accounts</span>
                      <strong>{metrics.accounts.size}</strong>
                    </div>
                  </div>
                  <div className="text-center" style={{ fontSize: '0.875rem', opacity: 0.9 }}>Click to view details →</div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default CloudMapTenantView;
